import { z } from "zod";
import type { LimitesAnexo } from "../../../types/portalCliente";
import { formatarTamanho } from "./severidade";

/**
 * Os limites de texto são as larguras das colunas do banco, que a API confere
 * antes de gravar. Repeti-los aqui é o que faz o aviso aparecer enquanto se
 * digita, e não depois de subir os anexos junto com o formulário.
 *
 * Os campos de seleção ficam como string porque é assim que saem do <select> e
 * assim que o multipart os envia — a API faz o int.TryParse do outro lado.
 */
export const novoChamadoSchema = z.object({
  idCategoria: z.string().min(1, "Selecione o tipo"),
  idProjeto: z.string().min(1, "Selecione o produto"),
  severidade: z.string().min(1, "Selecione a severidade"),
  assunto: z
    .string()
    .trim()
    .min(1, "Informe o assunto")
    .max(100, "O assunto vai até 100 caracteres"),
  descricao: z
    .string()
    .trim()
    .min(1, "Descreva a solicitação")
    .max(2000, "A descrição vai até 2000 caracteres"),
  nomeContato: z
    .string()
    .trim()
    .min(1, "Informe o solicitante")
    .max(50, "O nome vai até 50 caracteres"),
  emailContato: z
    .string()
    .trim()
    .min(1, "Informe o e-mail")
    .email("E-mail inválido")
    .max(100, "O e-mail vai até 100 caracteres"),
  receberInteracaoPorEmail: z.boolean(),
});

export type NovoChamadoForm = z.infer<typeof novoChamadoSchema>;

/** Categoria sem SLA: a procedure da fila não considera esses chamados. */
export const CATEGORIA_NOVAS_FUNCIONALIDADES = 2;

export const LIMITE_ASSUNTO = 100;
export const LIMITE_DESCRICAO = 2000;

/**
 * Mesmas recusas que a API faz, só que antes do envio: subir 25 MiB para
 * receber 413 é o pior jeito de descobrir que o arquivo não cabe.
 */
export const validarAnexos = (
  arquivos: File[],
  limites: LimitesAnexo
): string | null => {
  if (arquivos.length > limites.maxArquivos)
    return `São no máximo ${limites.maxArquivos} arquivos por chamado.`;

  const longo = arquivos.find((a) => a.name.length > 200);
  if (longo)
    return `O nome de "${longo.name.slice(0, 40)}…" passa de 200 caracteres.`;

  const grande = arquivos.find((a) => a.size > limites.maxBytesPorArquivo);
  if (grande)
    return `"${grande.name}" tem ${formatarTamanho(grande.size)} e o limite por arquivo é ${formatarTamanho(limites.maxBytesPorArquivo)}.`;

  const total = arquivos.reduce((soma, a) => soma + a.size, 0);
  if (total > limites.maxBytesTotal)
    return `A soma dos anexos é ${formatarTamanho(total)} e o limite por envio é ${formatarTamanho(limites.maxBytesTotal)}.`;

  return null;
};
